import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Form,
  Input,
  Button,
  Row,
  Col,
  Card,
  Divider,
  Space,
  Typography,
  Popconfirm,
  notification,
  Select,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import env from "../../env";
import paymentService from "../../services/payment";
import DefaultLayout from "../../components/layout/DefaultLayout";
import { Loader } from "@googlemaps/js-api-loader";

export const googleMapsLoader = new Loader({
  apiKey: env.REACT_APP_GOOGLE_API_KEY,
  version: "weekly",
  libraries: ["places"],
});

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

const Shipment = () => {
  const [miles, setMiles] = useState("0 mi");
  const [cartItems, setCartItems] = useState([]);
  const [boxes, setBoxes] = useState([{ weight: "", width: "", length: "", height: "" }]);
  const [editIndex, setEditIndex] = useState(null);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [googleInstance, setGoogleInstance] = useState(null);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const defaultOrigin = "1861 NW 22nd St, Miami, FL 33142, USA";

  const formRef = useRef(null);
  const cartRefs = useRef([]); // refs for cart items

  const calculateDistance = (destination, google) => {
    const service = new google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [defaultOrigin],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (status === "OK" && response.rows?.[0]?.elements?.[0]) {
          const element = response.rows[0].elements[0];
          if (element.status === "OK") setMiles(element.distance.text.replace(" mi", ""));
          else setMiles(null);
        }
      }
    );
  };

  const setupAutocomplete = (google) => {
    const input = document.getElementById("address-input");
    if (!input) return;
    const autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ["formatted_address", "geometry"],
    });
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place?.formatted_address) {
        form.setFieldsValue({ address: place.formatted_address });
        calculateDistance(place.formatted_address, google);
      }
    });
  };

  useEffect(() => {
    googleMapsLoader.load().then((google) => {
      setGoogleInstance(google);
      setupAutocomplete(google);
    });
  }, [form]);

  useEffect(() => {
    const storedCart = localStorage.getItem("cartItems");
    if (storedCart) setCartItems(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await paymentService.getOrders();
        if (response?.orders) {
          const uniqueCustomers = [];
          const seen = new Set();
          response.orders.forEach((order) => {
            const key = `${order.customer_name}-${order.whatsapp}`;
            if (!seen.has(key)) {
              seen.add(key);
              uniqueCustomers.push({
                customer_name: order.customer_name,
                whatsapp: order.whatsapp,
                address: order.address,
                boxes: order.boxes || [],
              });
            }
          });
          setRecentCustomers(uniqueCustomers);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      }
    };
    fetchOrders();
  }, []);

  const addBox = () => setBoxes([...boxes, { weight: "", width: "", length: "", height: "" }]);
  const updateBox = (index, field, value) => {
    const newBoxes = [...boxes];
    newBoxes[index][field] = value;
    setBoxes(newBoxes);
  };
  const deleteBoxFromForm = (index) => setBoxes(boxes.filter((_, i) => i !== index));

  const handleSelectCustomer = (value) => {
    const customer = recentCustomers.find(
      (c) => `${c.customer_name} - ${c.whatsapp}` === value
    );
    if (customer) {
      form.setFieldsValue({
        customerName: customer.customer_name,
        whatsapp: customer.whatsapp,
        address: customer.address,
      });
      if (googleInstance) calculateDistance(customer.address, googleInstance);
    }
  };

  const validateBoxes = () => {
    for (let i = 0; i < boxes.length; i++) {
      const { weight, width, length, height } = boxes[i];
      if (!weight || !width || !length || !height) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      const customerData = await form.validateFields();
      if (!validateBoxes()) {
        notification.error({
          message: "Incomplete Box Details",
          description: "Please fill weight, width, length, and height for all boxes.",
        });
        return;
      }
      const item = { ...customerData, boxes, miles };

      if (editIndex !== null) {
        const updatedCart = [...cartItems];
        updatedCart[editIndex] = item;
        setCartItems(updatedCart);
        setEditIndex(null);

        notification.success({
          message: "Cart Updated",
          description: `${customerData.customerName}'s shipment has been updated successfully.`,
          placement: "topRight",
        });

        // Scroll to the updated cart item
        setTimeout(() => {
          const el = cartRefs.current[editIndex];
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      } else {
        setCartItems([...cartItems, item]);
        notification.success({
          message: "Added to Cart",
          description: `${customerData.customerName}'s shipment has been added successfully.`,
          placement: "topRight",
        });
      }

      form.resetFields();
      setBoxes([{ weight: "", width: "", length: "", height: "" }]);
      if (googleInstance) setupAutocomplete(googleInstance);
    } catch {
      // validation errors handled by Ant Design
    }
  };

  const editCartItem = (index) => {
    const item = cartItems[index];
    form.setFieldsValue({
      customerName: item.customerName,
      whatsapp: item.whatsapp,
      address: item.address,
    });
    setBoxes(item.boxes);
    setEditIndex(index);
    if (googleInstance) calculateDistance(item.address, googleInstance);

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const deleteCartItem = (index) => setCartItems(cartItems.filter((_, i) => i !== index));

  const calculateShipping = (weight, miles) => {
    const w = parseFloat(weight) || 0;
    let basePrice = 0;
    if (w <= 40) basePrice = 30;
    else if (w <= 50) basePrice = 35;
    else if (w <= 60) basePrice = 40;
    else if (w <= 70) basePrice = 45;
    else if (w <= 80) basePrice = 50;
    else if (w <= 90) basePrice = 55;
    else if (w <= 100) basePrice = 60;
    else if (w <= 110) basePrice = 65;
    else if (w <= 120) basePrice = 70;
    else if (w <= 130) basePrice = 75;
    else if (w <= 140) basePrice = 80;
    else if (w <= 150) basePrice = 85;
    else basePrice = w * 0.55;

    let milesNumber = Math.floor(parseFloat(miles) || 0);
    let milesExtra = milesNumber > 10 ? (milesNumber - 10) * 1.5 : 0;
    return basePrice + milesExtra;
  };

  const totalAmount = cartItems
    .reduce((acc, item) => {
      const totalWeight = item.boxes.reduce((sum, box) => sum + parseFloat(box.weight || 0), 0);
      let milesValue = parseFloat(item.miles) || 0;
      return acc + calculateShipping(totalWeight, milesValue) * 1.05;
    }, 0)
    .toFixed(2);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <DefaultLayout>
        <Content style={{ padding: "50px 10%" }}>
          <Row gutter={[40, 40]} style={{ marginTop: 50 }}>
            <Col xs={24} md={12}>
              <div ref={formRef}>
                <Form form={form} layout="vertical">
                  <Form.Item label="Select Recent Customer">
                    <Select placeholder="Select past customer" allowClear onChange={handleSelectCustomer}>
                      {recentCustomers.map((c, index) => (
                        <Option key={index} value={`${c.customer_name} - ${c.whatsapp}`}>
                          {c.customer_name} - {c.whatsapp}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Form>

                <Card
                  className="my-card"
                  title="Shipment Form"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 4px rgba(0,0,0,0.1)",
                    border: "none",
                    background: "#fff2d6",
                  }}
                  headStyle={{
                    background: "linear-gradient(90deg,#3c3c3be6,#3c3c3be6)",
                    borderRadius: "12px 12px 0 0",
                  }}
                >
                  <Form form={form} layout="vertical">
                    <Form.Item
                      label="Customer Name"
                      name="customerName"
                      rules={[{ required: true, message: "Please enter customer name" }]}
                    >
                      <Input placeholder="Enter customer name" />
                    </Form.Item>
                    <Form.Item
                      label="Whatsapp#"
                      name="whatsapp"
                      rules={[{ required: true, message: "Please enter WhatsApp number" }]}
                    >
                      <Input placeholder="Enter WhatsApp number" />
                    </Form.Item>
                    <Form.Item
                      label="Drop-off Address"
                      name="address"
                      rules={[{ required: true, message: "Please enter address" }]}
                    >
                      <input
                        id="address-input"
                        placeholder="Enter drop-off address"
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                        }}
                      />
                    </Form.Item>

                    <Divider style={{ borderColor: "#fccc66e6" }}>Boxes</Divider>
                    {boxes.map((box, index) => (
                      <Card
                        key={index}
                        type="inner"
                        className="my-card2"
                        title={`Box ${index + 1}`}
                        extra={
                          <Button type="link" danger onClick={() => deleteBoxFromForm(index)}>
                            Delete
                          </Button>
                        }
                        style={{
                          marginBottom: 20,
                          borderRadius: "10px",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                          background: "#f4dba7e6",
                        }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item label="Weight (lbs)">
                              <Input
                                value={box.weight}
                                onChange={(e) => updateBox(index, "weight", e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Width (cm)">
                              <Input
                                value={box.width}
                                onChange={(e) => updateBox(index, "width", e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Length (cm)">
                              <Input
                                value={box.length}
                                onChange={(e) => updateBox(index, "length", e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Height (cm)">
                              <Input
                                value={box.height}
                                onChange={(e) => updateBox(index, "height", e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <Button type="dashed" block onClick={addBox} style={{ marginBottom: 20 }}>
                      + Add Box
                    </Button>

                    <Form.Item style={{ marginTop: 20 }}>
                      <Button
                        type="primary"
                        block
                        style={{
                          borderRadius: "8px",
                          background: "#3c3c3be6",
                          borderColor: "#3c3c3be6",
                        }}
                        onClick={handleSubmit}
                      >
                        {editIndex !== null ? "Update Cart" : "Add to cart"}
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <Card
                className="my-card"
                title="Cart Summary"
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  border: "none",
                  background: "#fff2d6",
                  marginTop: 60,
                }}
                headStyle={{
                  background: "linear-gradient(90deg,#3c3c3be6,#3c3c3be6)",
                  color: "#fff",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                {cartItems.map((item, index) => {
                  const totalWeight = item.boxes.reduce(
                    (sum, box) => sum + parseFloat(box.weight || 0),
                    0
                  );
                  let milesValue = parseFloat(item.miles) || 0;
                  const customerSubtotal = (
                    calculateShipping(totalWeight, milesValue) * 1.05
                  ).toFixed(2);

                  return (
                    <div key={index} ref={(el) => (cartRefs.current[index] = el)}>
                      <Space direction="vertical" style={{ width: "100%", marginBottom: 20 }}>
                        <Text strong>{item.customerName}</Text>
                        <Text>WhatsApp: {item.whatsapp}</Text>
                        <Text>Address: {item.address}</Text>
                        <Text type="secondary">Distance: {milesValue} mi</Text>
                        <Text>Total Weight: {totalWeight} lbs</Text>
                        <Text strong>Subtotal: ${customerSubtotal}</Text>
                        <Space>
                          <Button type="link" icon={<EditOutlined />} onClick={() => editCartItem(index)}>
                            Edit
                          </Button>
                          <Popconfirm
                            title="Are you sure you want to delete this item?"
                            onConfirm={() => deleteCartItem(index)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button type="link" icon={<DeleteOutlined />} danger>
                              Delete
                            </Button>
                          </Popconfirm>
                        </Space>
                        <Divider />
                      </Space>
                    </div>
                  );
                })}

                <Text strong>Total Customers: {cartItems.length}</Text>
                <br />
                <Text strong>Total Amount: ${totalAmount}</Text>

                <div style={{ marginTop: 20 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      borderRadius: "8px",
                      background: "#f4dba7e6",
                      borderColor: "#f4dba7e6",
                      color: "#3c3c3be6",
                    }}
                    onClick={() => navigate("/checkout")}
                  >
                    Checkout
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Content>
      </DefaultLayout>
    </Layout>
  );
};

export default Shipment;
